import { BOOKS_TIME_ZONE } from '../report-range'

/**
 * How the sales reports turn figures into text.
 *
 * Every clock time is read in the books' zone and on a 24-hour clock. The
 * legacy settlement export printed 14:19 as `02:19:29` with no AM or PM, and an
 * invoice that may have been rung at two in the morning or two in the
 * afternoon is one nobody can match to a receipt.
 */

/** `$1,941.10`, `−$4.02`. A dash only for a figure that was never recorded. */
export function usd(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—'

  const sign = amount < 0 ? '−' : ''

  return `${sign}$${Math.abs(amount).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`
}

/**
 * `60,000 ៛` — riel as counted, whole, never converted.
 *
 * Kept visibly apart from dollars rather than folded into them. Until a sale
 * records the rate it was taken at, any conversion done here would be a guess
 * dressed up as a figure — the legacy pack's "Cash (KHR)" column held a dollar
 * amount under a riel heading, which is exactly that guess.
 */
export function riel(amount: number | null | undefined): string {
  if (amount === null || amount === undefined) return '—'

  return `${Math.round(amount).toLocaleString('en-US')} ៛`
}

/** Cash in whichever currency it moved in. */
export function cash(
  amount: number | null | undefined,
  currency: string | null | undefined,
): string {
  if (amount === null || amount === undefined) return '—'

  return currency === 'KHR' ? riel(amount) : usd(amount)
}

/** `12`, `2.5` — a quantity, which can be fractional for anything sold by weight. */
export function quantity(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'

  return value.toLocaleString('en-US', { maximumFractionDigits: 2 })
}

/** `42` — a count, never a dash: zero of something is a real answer. */
export function whole(value: number | null | undefined): string {
  return (value ?? 0).toLocaleString('en-US')
}

/** `47.3%` from a 0–1 share. One decimal: item shares are often under one percent. */
export function share(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—'

  return `${(value * 100).toLocaleString('en-US', { maximumFractionDigits: 1 })}%`
}

/** `14 Oct, 14:19` — when a sale settled, in the outlet's clock. */
export function settledAt(iso: string | null | undefined): string {
  if (!iso) return '—'

  const at = new Date(iso)

  if (Number.isNaN(at.getTime())) return '—'

  return at.toLocaleString(undefined, {
    timeZone: BOOKS_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
}

const LEDGER_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/**
 * `01-Oct-2026` for a trading day, `Oct-2026` for a month — the legacy pack's
 * dates, for the sheets that are laid against it. Spelled out by hand so the
 * browser's locale cannot reorder it.
 */
export function ledgerPeriod(period: string | null | undefined): string {
  if (!period) return '—'

  const [year, month, day] = period.split('-')
  const name = LEDGER_MONTHS[Number(month) - 1]

  if (!year || !name) return period

  return day ? `${day.padStart(2, '0')}-${name}-${year}` : `${name}-${year}`
}

/**
 * `Tue 14 Oct` for a trading day, `Oct 2026` for a month.
 *
 * The server sends the day as `YYYY-MM-DD` — a calendar date, not an instant —
 * so it is formatted in UTC, where it was not shifted to begin with.
 */
export function tradingPeriod(period: string | null | undefined): string {
  if (!period) return '—'

  const [year, month, day] = period.split('-').map(Number)

  if (!year || !month) return period

  const at = new Date(Date.UTC(year, month - 1, day || 1))

  return day
    ? at.toLocaleDateString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      })
    : at.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' })
}
